<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import {
  formatRegistryDate,
  registryFieldGroups,
  registryPendencies,
} from '../composables/salesPropertyRegistryFields';
import SalesAICopyAlert from './SalesAICopyAlert.vue';
import SalesPropertyCertificateFeedback from './SalesPropertyCertificateFeedback.vue';
import SalesPropertyReferenceField from './SalesPropertyReferenceField.vue';

const props = defineProps({
  profile: { type: Object, required: true },
  errors: { type: Object, default: () => ({}) },
  catalog: { type: Object, default: () => ({}) },
  certificateChecks: { type: Array, default: () => [] },
  certificateChecksVisible: { type: Boolean, default: false },
  checkingCertificates: { type: Boolean, default: false },
});

const emit = defineEmits([
  'closeCertificateChecks',
  'checkCertificates',
  'update',
  'updateNested',
]);

defineOptions({ inheritAttrs: false });
const { t } = useI18n();
const groups = computed(() => registryFieldGroups(t));
const pendencies = computed(() => registryPendencies(t));
const options = computed(() => props.catalog.escrituras || []);

const update = (field, value) => {
  if (field === 'vagas') value = value.replace(/\D/g, '');
  if (field === 'lavrada') value = formatRegistryDate(value);
  emit('update', field, value);
};
const toggle = (field, value) =>
  emit('updateNested', 'pendenciasImovel', field, value);
const checkCertificates = field => {
  if (field === 'inscricaoMunicipal') emit('checkCertificates');
};
</script>

<template>
  <SalesPropertyCertificateFeedback
    :checks="certificateChecks"
    :visible="certificateChecksVisible"
    @close="emit('closeCertificateChecks')"
  />
  <LaisCard
    density="flush"
    class="self-start !border-0 !shadow-sm"
    data-test="sales-property-registry-section"
  >
    <div
      class="grid gap-5 px-8 pt-8 pb-8 font-roboto min-[701px]:pb-[18px] max-[700px]:gap-0 max-[700px]:p-8"
    >
      <h2
        class="font-lato text-[32px] font-bold leading-[39px] text-[#464F53] max-[700px]:mb-2 max-[700px]:text-[24px] max-[700px]:leading-[29px]"
      >
        {{ t('SALES_PROPERTY.REGISTRY.TITLE') }}
      </h2>
      <SalesAICopyAlert :message="t('SALES_PROPERTY.COMMON.AI_COPY_WARNING')" />
      <div
        v-for="(group, groupIndex) in groups"
        :key="groupIndex"
        class="grid gap-5 min-[701px]:gap-[15px] max-[700px]:gap-8"
        :class="[
          group.columns === 2
            ? 'min-[701px]:grid-cols-[minmax(0,1fr)_23%]'
            : 'min-[701px]:grid-cols-4',
          groupIndex === 0
            ? 'min-[701px]:mt-[9px] max-[700px]:mt-11'
            : 'min-[701px]:mt-3 max-[700px]:mt-8',
        ]"
        :data-test="`sales-property-registry-group-${groupIndex}`"
      >
        <SalesPropertyReferenceField
          v-for="(field, fieldIndex) in group.fields"
          :key="field.key"
          :label="field.label"
          :error="errors[field.key]"
          :required="field.required"
          :filled="Boolean(profile[field.key])"
          :indicator="field.select ? 'select' : 'success'"
          :disabled="field.key === 'inscricaoMunicipal' && checkingCertificates"
          :class="[
            group.columns === 2 && fieldIndex === group.fields.length - 1
              ? 'max-[700px]:w-[31%]'
              : '',
            groupIndex === 0 ? 'min-[701px]:min-h-[86px]' : '',
          ]"
        >
          <select
            v-if="field.select"
            :value="profile[field.key]"
            class="h-8 w-full appearance-none border-0 border-b border-[#009F94] bg-transparent bg-none px-0 pr-7 text-base font-bold text-[#009F94] outline-none"
            :data-test="`sales-property-registry-${field.key}`"
            @change="update(field.key, $event.target.value)"
          >
            <option value="" disabled>{{ field.placeholder }}</option>
            <option
              v-for="option in options"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
          <input
            v-else
            :value="profile[field.key]"
            :placeholder="field.placeholder"
            :disabled="
              field.key === 'inscricaoMunicipal' && checkingCertificates
            "
            :aria-busy="
              field.key === 'inscricaoMunicipal' && checkingCertificates
            "
            class="h-8 w-full border-0 border-b border-[#009F94] bg-transparent px-0 text-base font-bold text-[#009F94] outline-none placeholder:text-[#A7B7BE]"
            :data-test="`sales-property-registry-${field.key}`"
            @input="update(field.key, $event.target.value)"
            @blur="checkCertificates(field.key)"
          />
        </SalesPropertyReferenceField>
      </div>
      <fieldset class="min-[701px]:mt-3 max-[700px]:mt-8">
        <legend class="mb-5 text-xl leading-6 text-[#464F53]">
          {{ t('SALES_PROPERTY.REGISTRY.PENDENCIES_PREFIX') }}
          <strong>
            {{ t('SALES_PROPERTY.REGISTRY.PENDENCIES_EMPHASIS') }}
          </strong>
        </legend>
        <div class="flex flex-wrap gap-2.5">
          <label
            v-for="field in pendencies"
            :key="field.key"
            class="flex min-h-9 items-center gap-2 rounded-[10px] px-3 text-base text-[#464F53]"
            :class="
              profile.pendenciasImovel?.[field.key]
                ? 'bg-[#EDF8F7] text-[#009F94]'
                : ''
            "
          >
            <span class="relative grid size-[18px] shrink-0 place-items-center">
              <input
                type="checkbox"
                :checked="Boolean(profile.pendenciasImovel?.[field.key])"
                class="peer sr-only"
                :data-test="`sales-property-registry-pendency-${field.key}`"
                @change="toggle(field.key, $event.target.checked)"
              />
              <span
                class="size-[18px] rounded-[2px] border-[1.5px] border-[#01988C] bg-white peer-checked:bg-[#01988C]"
              />
              <span
                v-if="profile.pendenciasImovel?.[field.key]"
                class="i-lucide-check pointer-events-none absolute size-3 text-white"
                aria-hidden="true"
              />
            </span>
            {{ field.label }}
          </label>
        </div>
      </fieldset>
    </div>
    <div class="px-8 pb-8 max-[700px]:px-8">
      <slot name="footer" />
    </div>
  </LaisCard>
</template>
